import { useEffect, useState } from "react";
import type { Mode } from "../modes/orchestrator";
import type { DisplayStats, DataFreshness } from "../data/replay";
import { RegisteredCounter } from "./RegisteredCounter";

/**
 * How old the STALEST source mart may be before the wall admits it.
 *
 * Must be at least the server's DATA_STALE_THRESHOLD_MS (2h) — this badge now
 * measures the age of the DATA, not of the last fetch, and with hourly builds
 * the transaction high-water mark normally trails wall clock by up to an hour
 * plus source latency. A 90-minute threshold would light up during the normal
 * back half of every cycle and train people to ignore it.
 */
const STALE_THRESHOLD_MS = 2 * 60 * 60 * 1000;

/** Human label for the mart that is holding the feed back. */
const SOURCE_LABEL: Record<string, string> = {
  fct_transactions: "TRANSFERS",
  fct_virtual_cards__spend: "CARD SPEND",
  fct_virtual_cards__events: "CARD EVENTS",
  dim_users: "REGISTRATIONS",
};

export function BrandMark({
  mode,
  displayStats,
  freshness,
}: {
  mode: Mode;
  displayStats: DisplayStats | null;
  freshness: DataFreshness | null;
}) {
  const label =
    mode === "replay" ? "EVERSEND · REPLAY" : mode === "demo" ? "EVERSEND · DEMO" : "EVERSEND · LIVE";

  const avgDelivery = displayStats?.avgDeliverySec ?? 0;
  const totalRegistered = displayStats?.totalRegistered ?? 0;
  const ratePerMs = displayStats?.registrationsPerMs ?? 0;

  // Tick every 30s so the stale age ticks up visibly.
  const [, forceRender] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => forceRender((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // Age of the STALEST source, not of the last fetch. One frozen mart while
  // the others tick is exactly the case a fetch-time measure cannot see.
  const stalestTs = freshness?.stalestEventTs ?? null;
  const ageMs = stalestTs !== null ? Date.now() - stalestTs : null;
  // freshness present but no timestamp = a source reported nothing in 24h.
  const isStale =
    ageMs !== null ? ageMs > STALE_THRESHOLD_MS : freshness !== null;
  const staleMinutes = ageMs !== null ? Math.round(ageMs / 60_000) : 0;
  const staleSource = freshness?.stalestSource ?? null;
  const staleLabel = staleSource
    ? (SOURCE_LABEL[staleSource] ?? staleSource)
    : "DATA";

  return (
    <div className="brand-mark-wrap">
      <RegisteredCounter total={totalRegistered} ratePerMs={ratePerMs} />
      {avgDelivery > 0 && (
        <div className="delivery-badge">
          AVG DELIVERY · {Math.round(avgDelivery)}s
        </div>
      )}
      {isStale && (
        <div className="stale-badge">
          {staleLabel} STALE · {ageMs !== null ? `${staleMinutes}m` : "24h+"}
        </div>
      )}
      <div className="brand-mark">
        <span className="brand-dot" />
        {label}
      </div>
    </div>
  );
}
